import React, { FunctionComponent, useState, useEffect, useCallback } from "react";
import styles from "./News.module.scss";
import NewsProps from "./NewsProps.interface";

import fetchNews from "./hooks/fetchNews";
import NewsCard from "./components/NewsCard/NewsCard";

import { motion, useAnimation } from "framer-motion";
import { useHistory } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import BubbleBackground from "components/BubbleBackground/BubbleBackground";
import combineClasses from "helpers/combine-classes";
import Dictionary from "components/Dictionary/Dictionary";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import useAnchorIdentifier from "hooks/useAnchorIdentifier";
import isBrowserContext from "helpers/isBrowserContext";
import useFilter from "blocks/hooks/useFilter";

const News: FunctionComponent<NewsProps> = (props) => {
    const history = useHistory();

    const wrapperClasses = getWrapperClasses(props.settings);
    const identifier = useAnchorIdentifier(props.settings.anchorIdentifier);

    const [filters, filterViewState] = useFilter(props.content.filter);
    const [currentFilters, setCurrentFilters] = useState<any[]>();

    const { items, viewState } = fetchNews(props.content.tags);

    const clearFilters = () => {
        const query = new URLSearchParams(history.location.search);
        const activeBlock = query.get("b");

        setCurrentFilters([]);

        // Setting URL Params
        history.replace({
            pathname: location.pathname,
            search: `?b=${activeBlock}`,
            hash: location.hash
        });
    };

    const noFiltersClass = !currentFilters || (currentFilters && currentFilters.length === 0) ? styles.is_active : undefined;

    const filteredItems = useCallback(() => {
        const result = items ?? [];

        return result?.filter((item: any) => {
            const givenFilters = item.filters && item.filters.map((filter: any) => filter._id.toString());
            const currentFilter = currentFilters && currentFilters.length > 0 ? currentFilters[0] : undefined;

            return currentFilter ? givenFilters?.includes(currentFilter) : true;
        });
    }, [items, currentFilters]);

    const handleTagClick = (filter: any) => {
        const filterId = filter.id.toString();

        // Setting current filter
        if (typeof (filterId) !== "undefined" && filterId !== null) {
            if (currentFilters && currentFilters.length > 0 && currentFilters[0] === filterId) {
                setCurrentFilters([]);

                // Setting URL Params
                history.replace({
                    pathname: location.pathname,
                    search: undefined,
                    hash: location.hash
                });
            }
            else {
                setCurrentFilters([filterId]);

                // Setting URL Params
                history.replace({
                    pathname: location.pathname,
                    search: `?b=blockNewsList&f=${filter.id}`,
                    hash: location.hash
                });
            }
        }
    };

    useEffect(() => {
        const query = new URLSearchParams(history.location.search);
        const filter = query.get("f");

        if (typeof (filter) !== "undefined" && filter !== null) {
            setCurrentFilters([filter]);
        }
    }, []);

    // ANIMATION
    const controls = useAnimation();
    const { ref, inView } = useInView({ threshold: 0.2, });

    const animateUp = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: 80, opacity: 0 }
    };
    const animateDown = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: -80, opacity: 0 }
    };

    const selectAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content._contentType : undefined;
    const selectVariants = selectAnimation === "animateUp" ? animateUp : selectAnimation === "animateDown" ? animateDown : undefined;
    const repeatAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content.repeatAnimation : false;

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
        if (!inView && repeatAnimation) {
            controls.start("hidden");
        }
    }, [controls, inView]);

    return (
        <motion.section className={combineClasses([styles.wrapper, ...wrapperClasses])} id={identifier} ref={ref} initial={isBrowserContext() ? "hidden" : "shown"} animate={controls} variants={selectVariants} transition={{ duration: 0.4 }}>
            <div className={styles.container}>
                {/* Filters */}
                {isBrowserContext() && filterViewState === "LOADED" && filters && filters.length > 0 ? (
                    <div className={styles.tags}>
                        <button tabIndex={0} onClick={clearFilters} className={combineClasses([styles.tag, noFiltersClass])}>
                            <Dictionary name="NEWS_FILTER.ALL" />
                        </button>

                        {filters && filters?.map((filter: any, index: number) => {
                            const currentFilterClass = currentFilters?.includes(filter.id.toString()) ? styles.is_active : undefined;

                            return (
                                <button key={filter.id} tabIndex={index + 1} onClick={() => handleTagClick(filter)} className={combineClasses([styles.tag, currentFilterClass])}>
                                    {filter.label}
                                </button>
                            );
                        })}
                    </div>
                ) : null}

                {viewState === "LOADING" ? (
                    <div>
                        <div className={styles.loader}>Loading...</div>
                    </div>
                ) : null}

                {viewState === "LOADED" ? (
                    <div className={styles.list}>

                        {filteredItems().map((item: any) => {
                            return (
                                <NewsCard key={item._id} {...item} />
                            );
                        })}
                    </div>
                ) : null}

                {viewState === "EMPTY" ? (
                    <div className={styles.empty}>
                        <Dictionary name="NEWS_LIST.EMPTY" />
                    </div>
                ) : null}

                {viewState === "ERROR" ? (
                    <div className={styles.error}>
                        <Dictionary name="NEWS_LIST.ERROR" />
                    </div>
                ) : null}
            </div>

            {props.settings.bubbleBackground && props.settings.bubbleBackground.length > 0 ? (
                <BubbleBackground {...props.settings.bubbleBackground[0]} />
            ) : null}
        </motion.section>
    );
};

export default News;